import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ResourceType } from "@/types/resource";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Database } from "@/integrations/supabase/types";

interface ResourceSubmissionFormProps {
  mode: "receive" | "donate";
}

type DonationResourceType = Database['public']['Enums']['donation_resource_type'];

const resourceSubmissionSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  type: z.string().min(1, "Type is required"),
  address: z.string().min(1, "Location/address is required"),
  offer: z.string().min(1, "Offer details are required"),
  details: z.string().optional(),
});

type ResourceSubmissionValues = z.infer<typeof resourceSubmissionSchema>;

const ResourceSubmissionForm = ({ mode }: ResourceSubmissionFormProps) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<ResourceSubmissionValues>({
    resolver: zodResolver(resourceSubmissionSchema),
    defaultValues: {
      name: "",
      address: "",
      offer: "",
      details: "",
    },
  });

  const resourceTypes: ResourceType[] = ['shelter', 'food', 'grants', 'loans', 'medical', 'supplies', 'transportation', 'pets', 'storage', 'other'];
  const donationResourceTypes: DonationResourceType[] = ['volunteer', 'donate', 'buy', 'gofundme'];

  async function onSubmit(data: ResourceSubmissionValues) {
    try {
      const tableName = mode === "receive" ? 'pending_resources' : 'pending_donation_resources';
      const { error } = await supabase
        .from(tableName)
        .insert([
          {
            name: data.name,
            type: data.type,
            address: data.address,
            offer: data.offer,
            details: data.details,
            status: 'pending'
          }
        ]);

      if (error) throw error;

      toast({
        title: "Resource submitted successfully",
        description: "Your submission will be reviewed by our team.",
      });

      queryClient.invalidateQueries({ queryKey: ['resources', mode] });
      form.reset();
      setOpen(false);
    } catch (error) {
      toast({
        title: "Error submitting resource",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>{mode === "receive" ? "Submit Resource" : "Submit Donation"}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{mode === "receive" ? "Submit a Resource" : "Submit a Donation"}</DialogTitle>
          <DialogDescription>
            {mode === "receive" 
              ? "Share information about available resources to help others"
              : "Share information about resources you can donate"
            }
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company/Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Resource Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(mode === "receive" ? resourceTypes : donationResourceTypes).map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location/Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="offer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{mode === "receive" ? "What's being offered?" : "What can you donate?"}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={mode === "receive" 
                        ? "Describe the resource or service"
                        : "Describe what you can donate"
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Details (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional information"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit">Submit</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceSubmissionForm;